import SectionCard, { SectionCardGroup } from "../../../../Composites/SectionCard/SectionCard";
import { useEffect, useState } from "react";
import { GetWSMgr } from "@/Utils/StateLib";
import { FieldTextGroup, Flex, Icon, Pill, Button } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import { humanizeNumber } from "@coreapp/utils/humanizeNumber";
import { BoxChip } from "@/Utils/BoxChip";
import { QXDoc } from "@coreapp/CoreApp/QObjDB";
import { QIWizData } from "@coreapp/CoreApp/QIWizData";
import { QXDocMgr } from "@coreapp/CoreApp/QXDocMgr";
import { getBaseChartName, loadChartMetadata } from "@/Utils/Misc";
import { useAutoSyncHandler } from "@/hooks/useAutoSyncHandler";
import { useAutoSaveHandler } from "@/hooks/useAutoSaveHandler";
import { useStagingArea } from "@/appComponents/Navigation/RightSidebar/StagingArea/useStagingArea";
import { QPlanManager } from "@coreapp/CoreApp/QXWSMgr";
import { DefaultPlanObjective, PlanObjectiveColors } from "@/Utils/Plan/ObjectivesHandler";
import { DataSourceButton } from "../../ChartSettings/DataSourceConfiguration/DataSourceButton";
import { PositionConfig } from "@coreapp/CoreApp/PositionConfig";
import { ImportTypes } from "@/Utils/Constants";

type FieldTextObject = {
  field: string;
  value: string;
  afterTitle?: React.ReactNode;
}[];

enum ChartType {
  BaseChart = "Base Chart",
  Plan = "Plan",
  CustomChart = "Custom Chart"
}

enum DataField {
  View,
  Levels,
  TopBox,
  BoxCount,
  MainDataSource,
  BoxesShown,
  FiltersApplied,
  DisconnectedRecords,

  SourceBaseChart,
  PlanObjective,

  AutoSyncEnabled,
  StagedRecords,
  AutoSaveEnabled
}

const CHART_PILL_LABELS = {
  [ChartType.BaseChart]: {
    position: "Base Position Chart",
    employee: "Base Employee Chart"
  },
  [ChartType.Plan]: {
    position: "Position Chart Plan",
    employee: "Employee Chart Plan"
  },
  [ChartType.CustomChart]: {
    position: "Custom Position Chart",
    employee: "Custom Employee Chart"
  }
};

const FIELDS_ORDER = {
  [ChartType.BaseChart]: [
    DataField.View,
    DataField.TopBox,
    DataField.Levels,
    DataField.BoxCount,
    DataField.BoxesShown,
    DataField.FiltersApplied,
    DataField.DisconnectedRecords
  ],
  [ChartType.Plan]: [
    DataField.SourceBaseChart,
    DataField.Levels,
    DataField.View,
    DataField.BoxCount,
    DataField.TopBox,
    DataField.BoxesShown,
    DataField.PlanObjective,
    DataField.FiltersApplied
  ],
  [ChartType.CustomChart]: [
    DataField.SourceBaseChart,
    DataField.BoxCount,
    DataField.View,
    DataField.BoxesShown,
    DataField.TopBox,
    DataField.FiltersApplied,
    DataField.AutoSyncEnabled,
    DataField.StagedRecords,
    DataField.AutoSaveEnabled
  ]
};

type FieldGetters = {
  [key in DataField]: (
    curDoc: QXDoc,
    iWiz: QIWizData,
    docMgr: QXDocMgr,
    planMgr: Nullable<QPlanManager>,
    controllerData?: any
  ) => React.ReactNode | number | undefined;
};

const FIELD_GETTERS: FieldGetters = {
  [DataField.View]: curDoc => curDoc.GetActiveTemplate()?.GetTemplateName(),
  [DataField.Levels]: curDoc => curDoc.GetRequestedLevels()?.toString(),
  [DataField.TopBox]: BoxChip.topBoxLabelFromDoc,
  [DataField.BoxCount]: (curDoc, iWiz) =>
    humanizeNumber(curDoc.IsServerChart() ? iWiz.GetBaseBoxCount() : curDoc.GetCurBoxCount()),
  [DataField.MainDataSource]: (_, iWiz) => iWiz?.GetDataSource() ?? -1,
  [DataField.BoxesShown]: curDoc => {
    const cursubchart = curDoc.GetSubChartMgr()?.GetCurrentSubChartID();
    const curCount = curDoc
      .GetSubChartMgr()
      ?.GetNodesForSubChart(cursubchart)
      ?.filter((node: any) => node.fDataObj !== null).length;
    return humanizeNumber(curCount ?? 0);
  },
  [DataField.FiltersApplied]: (curDoc, iWiz) => {
    const activeSavedIntFilters = curDoc.GetFilterMgr()?.TotalNumberOfActiveFilters();
    if (!curDoc.IsServerChart()) {
      return activeSavedIntFilters;
    } else {
      return activeSavedIntFilters + (iWiz.GetFilterRule() ? 1 : 0);
    }
  },
  [DataField.DisconnectedRecords]: curDoc => curDoc.CountOrphans(),
  [DataField.SourceBaseChart]: curDoc => getBaseChartName(curDoc.GetChartID()),
  [DataField.PlanObjective]: (_, __, ___, planMgr) => {
    const planInfo = planMgr?.GetPlanInfo();
    const objective = planInfo?.objective ?? DefaultPlanObjective;
    return <Pill label={appstr(objective)} color={PlanObjectiveColors[objective]} />;
  },
  [DataField.AutoSyncEnabled]: (_, __, ___, ____, controllerData) =>
    controllerData.autoSyncEnabled ? appstr("Yes") : appstr("No"),
  [DataField.StagedRecords]: (_, __, ___, ____, controllerData) =>
    controllerData.branches.length > 0
      ? controllerData.branches.reduce(
          (acc: number, branch: any) => acc + branch.SmartGetFieldValue("Headcount"),
          0
        )
      : appstr("None"),
  [DataField.AutoSaveEnabled]: (_, __, ___, ____, controllerData) =>
    controllerData.autoSaveEnabled ? appstr("Yes") : appstr("No")
};

const FIELD_MESSAGES = {
  [DataField.Levels]: "Levels tooltip",
  [DataField.BoxCount]: "Box count tooltip",
  [DataField.BoxesShown]: "Boxes shown tooltip",
  [DataField.FiltersApplied]: "Filters applied tooltip",
  [DataField.DisconnectedRecords]: "Disconnected records tooltip",
  [DataField.StagedRecords]: "All staged records tooltip",
  [DataField.AutoSaveEnabled]: undefined,
  [DataField.AutoSyncEnabled]: undefined,
  [DataField.SourceBaseChart]: undefined,
  [DataField.PlanObjective]: undefined,
  [DataField.View]: undefined,
  [DataField.TopBox]: undefined,
  [DataField.MainDataSource]: undefined
};

const FIELD_LABELS = {
  [DataField.View]: "Current view applied",
  [DataField.Levels]: "Levels Shown",
  [DataField.TopBox]: "Current top box",
  [DataField.BoxCount]: "boxcount",
  [DataField.SourceBaseChart]: "Source base chart",
  [DataField.MainDataSource]: "Main Data Source",
  [DataField.BoxesShown]: "Number of Boxes",
  [DataField.FiltersApplied]: "Filters Applied",
  [DataField.DisconnectedRecords]: "orphanrecords",
  [DataField.PlanObjective]: "planobjective",
  [DataField.AutoSyncEnabled]: "Auto-sync enabled",
  [DataField.AutoSaveEnabled]: "Auto-save enabled",
  [DataField.StagedRecords]: "All staged records"
};

export const ChartDetails = () => {
  const wsMgr = GetWSMgr();
  const curDoc = wsMgr?.GetDoc();
  const docMgr = wsMgr?.GetDocMgr();
  const iWiz = docMgr?.GetIWizDataRefresh();
  const isPlan = curDoc.IsPlanChart();
  const planMgr = wsMgr?.GetPlanMgr();
  const isCustomChart = !curDoc.IsServerChart() && iWiz?.GetDataSource();
  const isBaseChart = !isPlan && !isCustomChart;

  const { autoSyncEnabled } = useAutoSyncHandler({ doc: curDoc });
  const { autoSaveEnabled } = useAutoSaveHandler({ doc: curDoc, iWiz });
  const { branches } = useStagingArea();
  const controllerData = {
    autoSyncEnabled,
    autoSaveEnabled,
    branches
  };

  const chartType = isBaseChart
    ? ChartType.BaseChart
    : isPlan
      ? ChartType.Plan
      : ChartType.CustomChart;

  useEffect(() => {
    const eventTracker = wsMgr?.GetUsageTracker();
    eventTracker?.ReportEvent("chart_settings.chart_details.opened");
  }, []);

  const addFieldText = (
    field: string,
    value: string | number | React.ReactNode,
    data: object[],
    message?: string
  ) => {
    const infoIcon = message === undefined ? "" : <MoreInfoTooltip message={message} />;

    data.push({ field: field, value: value, afterTitle: infoIcon });
  };

  const getData = () => {
    // const planMgr = wsMgr?.GetPlanMgr();
    const data: FieldTextObject = [];

    const fieldOrder = FIELDS_ORDER[chartType];
    fieldOrder.forEach(field => {
      const fieldLabel = appstr(FIELD_LABELS[field]);
      const fieldValue =
        FIELD_GETTERS[field](curDoc, iWiz, docMgr, planMgr || null, controllerData) ?? "--";
      const msg = FIELD_MESSAGES[field];
      const fieldMessage = msg
        ? appstr(msg, { document: appstr(chartType).toLowerCase() })
        : undefined;

      addFieldText(fieldLabel, fieldValue, data, fieldMessage);
    });

    return data;
  };

  return (
    <SectionCardGroup customClass="ocn-chart-details-card-group">
      <SectionCard
        titleContent={<ChartDetailsHeader chartType={chartType} />}
        headerFlexProps={{
          height: "fit-content",
          justifyContent: "flex-start",
          padding: "0px"
        }}
        flexProps={{
          gap: "16px"
        }}
        childWrapperClass={{ padding: "0px" }}
      >
        <Flex direction={"row"} gap={"24px"} height={"100%"}>
          {isBaseChart && <DataSources doc={curDoc} />}
          <FieldTextGroup
            columnGap={200}
            columns={isBaseChart ? 1 : 2}
            data={getData()}
            size={"large"}
          />
        </Flex>
      </SectionCard>
    </SectionCardGroup>
  );
};

const ChartDetailsHeader = (props: { chartType: ChartType }) => {
  const { chartType } = props;
  const wsMgr = GetWSMgr();
  const curDoc = wsMgr?.GetDoc();
  const planMgr = wsMgr?.GetPlanMgr();
  const docMgr = wsMgr?.GetDocMgr();
  const iWiz = docMgr?.GetIWizDataRefresh();

  const isPositionChart = curDoc.IsPositionDocument();
  const chartPillString = appstr(
    CHART_PILL_LABELS[chartType][isPositionChart ? "position" : "employee"]
  );
  const chartPillIcon = isPositionChart ? "cubes" : "image-user";

  const syncDate =
    chartType === ChartType.Plan ? planMgr?.GetLastSyncLabel(true) : curDoc.GetRefreshDateValue();
  const description = curDoc.IsPlanChart() ? planMgr?.GetPlanDescription() : iWiz?.GetDescription();

  return (
    <Flex direction={"column"} gap={"16px"} height={"100%"}>
      {/* Chip & Last Refreshed */}
      <Flex alignItems="center" className="ocn-chart-details--refresh-and-pill">
        <Pill label={chartPillString} leftContent={<Icon name={chartPillIcon} size="small" />} />
        <p>
          {appstr(chartType === ChartType.BaseChart ? "lastrefresh" : "Last Sync")}: {syncDate}
        </p>
      </Flex>

      <div className="ocn-chart-details--name-desc">
        {/* Chart name */}
        <h2>{docMgr.GetFileName(false) || appstr("Untitled")}</h2>

        {/* Chart desc */}
        <p className={Boolean(description) ? "" : "ocn-chart-details--no-description"}>
          {description || appstr("No description").toLowerCase()}
        </p>
      </div>
    </Flex>
  );
};

// Info Tooltip
export const MoreInfoTooltip = (props: { message: string }) => {
  const { message } = props;
  return (
    <Button
      tooltipProps={{ message, position: "right-start", delay: 100 }}
      modifier={"tertiary"}
      iconOnly={true}
      iconProps={{ name: "circle-info" }}
    />
  );
};

// Data Sources panel and items component -- only for Base Charts
const DataSources = (props: { doc: QXDoc }) => {
  const [docMetadata, setDocMetadata] = useState<any>({});
  const positionConfig = new PositionConfig(docMetadata);
  const multiSourceMode =
    positionConfig.isPositionChart() && positionConfig.isPositionDataIngestMultiple();

  useEffect(() => {
    (async () => {
      const docID = props.doc.GetChartID();
      if (docID === null) return;
      setDocMetadata(await loadChartMetadata(docID));
    })();
  }, []);

  const employeeSource = multiSourceMode ? docMetadata?.import?.holders : docMetadata?.import;

  return (
    <Flex direction="column" gap="8px" width="400px" className="ocn-chart-details--data-sources">
      <Flex direction="column" gap="8px">
        <h4>{appstr("Main Data Source")}</h4>
        <>
          {multiSourceMode && <h5>{appstr("Employee dataset")}</h5>}
          <DataSourceItem
            docMetadata={docMetadata}
            config={employeeSource}
            type={ImportTypes.import}
          />
          {!employeeSource && <p>--</p>}
        </>
        {multiSourceMode && (
          <>
            <h5>{appstr("Position dataset")}</h5>
            <DataSourceItem
              docMetadata={docMetadata}
              config={docMetadata?.import?.positions}
              type={ImportTypes.import}
            />
            {!docMetadata?.import?.positions && <p>--</p>}
          </>
        )}
      </Flex>

      <Flex direction="column" gap="8px">
        <h4>{appstr("Append data sources")}</h4>
        {docMetadata?.append?.map((append: any) => (
          <DataSourceItem docMetadata={docMetadata} config={append} type={ImportTypes.append} />
        ))}
        {!docMetadata?.append?.length && <p>--</p>}
      </Flex>

      <Flex direction="column" gap="8px">
        <h4>{appstr("Merge data sources")}</h4>
        {docMetadata?.merge?.map((merge: any) => (
          <DataSourceItem docMetadata={docMetadata} config={merge} type={ImportTypes.merge} />
        ))}
        {!docMetadata?.merge?.length && <p>--</p>}
      </Flex>
    </Flex>
  );
};

type DataSourceItemProps = {
  config: any;
  type: string;
  docMetadata: any;
};

const DataSourceItem = (props: DataSourceItemProps) => {
  const { config, type, docMetadata } = props;
  if (!config) return null;
  return (
    <DataSourceButton
      chartConfig={docMetadata}
      dataSource={config.source}
      type={type}
      removeBackground
      labelProps={{ size: "medium" }}
      fillWidth
      layout="inline"
      config={config}
      imageWidth={24}
      imageHeight={24}
      compactStyle
    />
  );
};
